/**
 * Public NFC/QR review redirect endpoint.
 *
 * Contract (per spec):
 *  1. No interstitial UI — just redirect immediately.
 *  2. Redirect happens even if the tracking call fails or the network is slow.
 *  3. Tracking is fired via navigator.sendBeacon() so it never blocks the
 *     navigation. As a fallback we use fetch({ keepalive: true }).
 *  4. Reads GOOGLE_REVIEW_URL from the backend /api/nfc/config with a short
 *     timeout, and falls back to a hardcoded default (updated centrally on
 *     the server anyway — this is just a safety net).
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const FALLBACK_REVIEW_URL = "https://g.page/r/CRvqLSLtMh52EBM/review";
const CONFIG_TIMEOUT_MS = 400;

function fireTrack(payload) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/nfc/track`;
    const body = JSON.stringify(payload);
    try {
        if (
            typeof navigator !== "undefined" &&
            typeof navigator.sendBeacon === "function"
        ) {
            const blob = new Blob([body], { type: "application/json" });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) return;
        }
    } catch (_e) {
        // fall through
    }
    // Fallback — keepalive so the request survives navigation
    try {
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
            credentials: "omit",
        }).catch(() => {});
    } catch (_e) {
        // last-resort noop
    }
}

async function resolveTarget() {
    try {
        const controller = new AbortController();
        const timer = setTimeout(
            () => controller.abort(),
            CONFIG_TIMEOUT_MS
        );
        const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/nfc/config`,
            { signal: controller.signal, credentials: "omit" }
        );
        clearTimeout(timer);
        if (res.ok) {
            const data = await res.json();
            if (data?.google_review_url) return data.google_review_url;
        }
    } catch (_e) {
        // fall through to fallback
    }
    return FALLBACK_REVIEW_URL;
}

export default function ReviewRedirect() {
    const [params] = useSearchParams();

    useEffect(() => {
        const t = (params.get("t") || "").trim();
        const sourceRaw = (params.get("source") || "").toLowerCase().trim();
        const source =
            sourceRaw === "nfc" || sourceRaw === "qr" ? sourceRaw : "unknown";

        if (t) fireTrack({ t, source });

        // Redirect in parallel — do not wait on tracking
        resolveTarget().then((url) => {
            window.location.replace(url);
        });
    }, [params]);

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                background: "#f4f7fa",
                color: "#1a1a1a",
                fontSize: 15,
                letterSpacing: "0.02em",
            }}
            data-testid="review-redirect-page"
        >
            <span>Reindirizzamento a Google…</span>
        </div>
    );
}
